import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { useChatStore, startGuestSession } from '../chat/state/chatStore'
import { guestAsk } from '../chat/api/chatApiClient'
import { api } from '../api'
import FigureHeader from '../components/FigureHeader.jsx'
import QuothaAvatar from '../components/quotha/QuothaAvatar'
import { ensureValidFigureSlug, OUT_OF_DATE_FIGURE_MESSAGE } from '../utils/figureSlugRegistry'
import TypewriterReveal from '../chat/components/TypewriterReveal'
import TypingAnimationToggle from '../chat/components/TypingAnimationToggle'
import useTypingAnimationPreference from '../chat/hooks/useTypingAnimationPreference'

const LOGIN_ROUTE = '/login'
const REGISTER_ROUTE = '/register'

const formatSlugToName = (slug) => {
  if (!slug) {
    return null
  }
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')
}

const GuestChat = () => {
  const { slug: routeSlug } = useParams()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const querySlug = searchParams.get('figure_slug')
  const figureSlug = (routeSlug ?? querySlug ?? '').trim() || null
  const placeSlug = searchParams.get('place_slug')
  const presetPrompt = searchParams.get('prompt')?.trim() ?? ''
  const { state, dispatch } = useChatStore()
  const {
    mode,
    loading,
    error,
    messages,
    currentThreadId,
    guestMode,
    sessionStarted,
    sessionId,
    activeFigureSlug,
    activePlaceSlug,
    maxQuestions,
    remainingQuestions,
    limitReached,
    serviceError,
  } = state
  const [inputValue, setInputValue] = useState('')
  const [sending, setSending] = useState(false)
  const [figure, setFigure] = useState(null)
  const [figureLoading, setFigureLoading] = useState(false)
  const [figureError, setFigureError] = useState(null)
  const messagesEndRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const pinnedToBottomRef = useRef(true)
  const animatedAssistantMessageIdsRef = useRef(new Set())
  const initialAssistantSnapshotRef = useRef(
    (messages ?? [])
      .filter((msg) => msg?.role === 'assistant' && typeof msg?.id === 'string')
      .map((msg) => msg.id),
  )
  const {
    typingAnimationEnabled,
    typingAnimationPreference,
    typingAnimationBlocked,
    setTypingAnimationEnabled,
  } = useTypingAnimationPreference()
  const sessionReady = guestMode && sessionStarted
  const isPreparing = mode === 'guest-starting' || (loading && !sessionReady)
  const hasError = Boolean(error)
  const normalizedRemaining =
    typeof remainingQuestions === 'number' ? Math.max(remainingQuestions, 0) : null
  const showGuestCounter = typeof normalizedRemaining === 'number' && typeof maxQuestions === 'number'
  const noQuestionsLeft = typeof remainingQuestions === 'number' && remainingQuestions <= 0
  const guestLimitReached = limitReached || noQuestionsLeft
  const shouldShowLimitPanel = limitReached || (typeof maxQuestions === 'number' && noQuestionsLeft)
  const quotaUnavailable = serviceError === 'quota'
  const sessionMissing = sessionReady && !sessionId
  const assistantName = figure?.name ?? formatSlugToName(figureSlug) ?? 'History Guide'
  const guestPassMessage = showGuestCounter
    ? normalizedRemaining > 0
      ? `Guest Pass: ${normalizedRemaining} of ${maxQuestions} questions left`
      : 'Guest Pass complete - register or log in for more questions'
    : null
  const sessionMissingMessage = sessionMissing
    ? "We couldn't start your guest session. Please refresh the page and try again."
    : null

  useEffect(() => {
    initialAssistantSnapshotRef.current.forEach((id) => {
      if (typeof id === 'string' && id.length) {
        animatedAssistantMessageIdsRef.current.add(id)
      }
    })
  }, [])

  useEffect(() => {
    if (mode === 'guest-starting') {
      animatedAssistantMessageIdsRef.current.clear()
    }
  }, [mode])

  const scrollToBottom = useCallback((behavior = 'smooth', force = false) => {
    if (!messagesEndRef.current) {
      return
    }
    if (!force && !pinnedToBottomRef.current) {
      return
    }
    messagesEndRef.current.scrollIntoView({ behavior, block: 'end' })
  }, [])

  useEffect(() => {
    const node = scrollContainerRef.current
    if (!node) {
      return undefined
    }

    const handleScroll = () => {
      const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight
      pinnedToBottomRef.current = distanceFromBottom < 64
    }

    node.addEventListener('scroll', handleScroll)
    return () => {
      node.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    const node = scrollContainerRef.current
    if (!node || typeof ResizeObserver === 'undefined') {
      return undefined
    }

    const observer = new ResizeObserver(() => {
      if (pinnedToBottomRef.current) {
        scrollToBottom('auto', true)
      }
    })

    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [scrollToBottom])

  useEffect(() => {
    if (!figureSlug) {
      setFigure(null)
      setFigureError(null)
      setFigureLoading(false)
      return
    }

    const normalizedPlace = placeSlug ?? null
    const alreadyActive =
      sessionReady &&
      activeFigureSlug === figureSlug &&
      (activePlaceSlug ?? null) === normalizedPlace

    if (mode === 'guest-starting' || alreadyActive) {
      return
    }

    startGuestSession(dispatch, figureSlug, normalizedPlace)
  }, [dispatch, figureSlug, placeSlug, sessionReady, activeFigureSlug, activePlaceSlug, mode])

  useEffect(() => {
    if (!figureSlug) {
      return
    }

    let isMounted = true
    setFigureLoading(true)
    setFigureError(null)

    ensureValidFigureSlug(figureSlug)
      .then((isValid) => {
        if (!isMounted) return null
        if (!isValid) {
          setFigure(null)
          setFigureError(OUT_OF_DATE_FIGURE_MESSAGE)
          setFigureLoading(false)
          return null
        }
        return api.getFigureLite(figureSlug)
      })
      .then((data) => {
        if (!isMounted || !data) {
          return
        }
        setFigure(data)
      })
      .catch((err) => {
        if (!isMounted) {
          return
        }
        console.error('[GUEST_CHAT] figure fetch failed', err)
        setFigure(null)
        setFigureError('We could not load the full profile right now.')
      })
      .finally(() => {
        if (isMounted) {
          setFigureLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [figureSlug])

  useEffect(() => {
    scrollToBottom('smooth')
  }, [messages, sending, scrollToBottom])

  useEffect(() => {
    if (presetPrompt) {
      setInputValue(presetPrompt)
    }
  }, [presetPrompt])

  const handleSubmit = async (event) => {
    event.preventDefault()
    const trimmed = inputValue.trim()

    if (!trimmed || !sessionReady || guestLimitReached || quotaUnavailable) {
      return
    }

    if (!sessionId) {
      dispatch({
        type: 'SET_ERROR',
        payload: "We couldn't start your guest session. Please refresh the page and try again.",
      })
      return
    }

    try {
      setSending(true)
      dispatch({
        type: 'ADD_MESSAGE',
        payload: {
          id: `user-${Date.now()}`,
          role: 'user',
          content: trimmed,
          created_at: new Date().toISOString(),
        },
      })
      setInputValue('')

      const res = await guestAsk(trimmed, sessionId, currentThreadId ?? null)
      const answer =
        res?.answer ??
        res?.message ??
        res?.content ??
        (Array.isArray(res?.messages) ? res.messages.map((msg) => msg?.content).filter(Boolean).join('\n\n') : '')

      if (answer) {
        dispatch({
          type: 'ADD_MESSAGE',
          payload: {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: answer,
            created_at: new Date().toISOString(),
          },
        })
      }

      dispatch({ type: 'SET_ERROR', payload: null })

      const updatedRemaining = res?.remaining_questions ?? res?.remainingQuestions ?? normalizedRemaining ?? null
      const updatedMax = res?.max_questions ?? res?.maxQuestions ?? maxQuestions ?? null

      if (updatedRemaining !== null || updatedMax !== null) {
        dispatch({
          type: 'UPDATE_GUEST_LIMITS',
          payload: {
            remainingQuestions: updatedRemaining,
            maxQuestions: updatedMax,
          },
        })
      }

      dispatch({
        type: 'SET_LIMIT_STATUS',
        payload: { limitReached: false, serviceError: null },
      })
    } catch (err) {
      const errorType = err?.type
      if (errorType === 'guest_limit_reached') {
        const errDetail = err?.detail ?? {}
        const limitPayload = {
          remainingQuestions: 0,
          maxQuestions: errDetail?.max_questions ?? errDetail?.maxQuestions ?? maxQuestions ?? null,
        }
        dispatch({ type: 'UPDATE_GUEST_LIMITS', payload: limitPayload })
        dispatch({
          type: 'SET_LIMIT_STATUS',
          payload: { limitReached: true, serviceError: null },
        })
        dispatch({
          type: 'SET_ERROR',
          payload: err?.message ?? 'Your guest pass is used up. Log in or register to keep chatting.',
        })
      } else if (errorType === 'llm_quota') {
        dispatch({
          type: 'SET_LIMIT_STATUS',
          payload: { limitReached: false, serviceError: 'quota' },
        })
        dispatch({
          type: 'SET_ERROR',
          payload:
            err?.message ?? 'Our AI guide is temporarily unavailable because of high demand. Please try again later.',
        })
      } else if (errorType === 'network') {
        dispatch({
          type: 'SET_ERROR',
          payload: err?.message ?? 'We could not reach the chat service. Check your connection and try again.',
        })
      } else if (err?.errorCode === 'guest_session_missing') {
        dispatch({ type: 'SET_SESSION_TOKEN', payload: null })
        dispatch({
          type: 'SET_ERROR',
          payload: 'Your guest session expired or could not be found. Please refresh the page to start a new chat.',
        })
      } else {
        const errorDetails = [err?.status ? `status ${err.status}` : null, err?.errorCode ? `code ${err.errorCode}` : null]
          .filter(Boolean)
          .join(', ')
        const suffix = errorDetails ? ` (${errorDetails})` : ''
        dispatch({
          type: 'SET_ERROR',
          payload: `Something went wrong sending your message${suffix}. Please try again.`,
        })
      }
    } finally {
      setSending(false)
    }
  }

  const disabled =
    sending || isPreparing || !sessionReady || guestLimitReached || quotaUnavailable || sessionMissing || !figureSlug

  const renderedMessages = messages.map((msg, index) => {
    const messageId = msg.id ?? `${msg.role}-${index}`
    const isAssistant = msg.role === 'assistant'
    const alreadyAnimated = animatedAssistantMessageIdsRef.current.has(messageId)
    const shouldAnimateAssistant = isAssistant && typingAnimationEnabled && !typingAnimationBlocked && !alreadyAnimated

    if (isAssistant && !shouldAnimateAssistant && !alreadyAnimated) {
      animatedAssistantMessageIdsRef.current.add(messageId)
    }

    const roleClass = msg.role === 'user' ? 'user' : 'assistant'
    return (
      <div key={messageId} className={`chat-message chat-message-${roleClass}`}>
        <div className="chat-message-meta">
          <span className="chat-message-role">{msg.role === 'user' ? 'You' : assistantName}</span>
        </div>
        <div className="chat-message-content">
          {isAssistant ? (
            <TypewriterReveal
              text={msg.content}
              enabled={shouldAnimateAssistant}
              className="typewriter-block"
              onDone={() => {
                animatedAssistantMessageIdsRef.current.add(messageId)
                scrollToBottom('auto')
              }}
            />
          ) : (
            msg.content
          )}
        </div>
      </div>
    )
  })

  return (
    <section className="chat-page">
      <p className="eyebrow">Talk to History</p>
      <h1>Places in Time Chat</h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
        <QuothaAvatar size={108} paused={isPreparing || sending} />
        <p className="chat-cta-note" style={{ margin: 0 }}>
          Quotha keeps watch while you start your guest chat.
        </p>
      </div>
      <FigureHeader
        figure={figure}
        loading={figureLoading && Boolean(figureSlug)}
        error={figureError}
        slug={figureSlug ?? ''}
      />
      {!figureSlug && <p className="chat-error">Choose a historical figure to begin chatting.</p>}
      {hasError && <p className="chat-error">{error}</p>}
      {sessionMissingMessage && <p className="chat-error">{sessionMissingMessage}</p>}
      {isPreparing && figureSlug && <p className="chat-status">Preparing your guest chat session...</p>}
      {guestPassMessage && <p className="chat-limit-note">{guestPassMessage}</p>}
      {quotaUnavailable && (
        <div className="chat-quota-banner">
          Our AI guide is temporarily unavailable (usage limit reached). Please try again later.
        </div>
      )}
      {shouldShowLimitPanel && (
        <div className="chat-limit-panel">
          <p>Your guest pass is used up. Log in or register to continue the conversation.</p>
          <div className="chat-limit-actions">
            <Link className="button" to={LOGIN_ROUTE}>
              Log in
            </Link>
            <Link className="button" to={REGISTER_ROUTE}>
              Sign up
            </Link>
          </div>
        </div>
      )}
      <div className="chat-shell">
        <div className="chat-messages" ref={scrollContainerRef}>
          {messages.length === 0 && (
            <p className="chat-empty">Ask a question to begin — for example, "What was daily life like here?"</p>
          )}
          {renderedMessages}
          {sending && sessionReady && (
            <div className="chat-message chat-message-assistant typing">
              <div className="chat-message-meta">
                <span className="chat-message-role">{assistantName}</span>
              </div>
              <div className="chat-message-content">
                <div className="chat-typing-indicator" role="status" aria-live="polite">
                  <span className="chat-typing-label">{assistantName} is thinking...</span>
                  <span className="chat-typing-dots" aria-hidden="true">
                    <span className="chat-typing-dot" />
                    <span className="chat-typing-dot" />
                    <span className="chat-typing-dot" />
                  </span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <TypingAnimationToggle
          enabled={typingAnimationEnabled}
          preference={typingAnimationPreference}
          blocked={typingAnimationBlocked}
          onToggle={setTypingAnimationEnabled}
          description="Make assistant replies type in smoothly."
          className="chat-typing-toggle-inline"
        />
        <form className="chat-input-row" onSubmit={handleSubmit}>
          <input
            type="text"
            className="chat-input"
            placeholder={
              !figureSlug
                ? 'Select a figure to begin.'
                : sessionReady
                ? 'Ask your question...'
                : isPreparing
                ? 'Setting up your guest session...'
                : 'Chat is unavailable right now.'
            }
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            disabled={disabled}
          />
          <button type="submit" className="button primary" disabled={disabled}>
            {sending ? 'Sending...' : 'Send'}
          </button>
        </form>
      </div>
    </section>
  )
}

export default GuestChat
